/**
 * Routing a tapped push notification.
 *
 * Split out of native-push.ts so the decision logic is testable without a device: this
 * module owns "given a tap payload, where do we go and what do we set first", and
 * native-push.ts owns only the plugin wiring.
 *
 * ── COLD START ──────────────────────────────────────────────────────────────
 *
 * The dangerous case is a tap that launches the app from cold: the native side fires
 * `notificationActionPerformed` while the web view is still booting, so a listener
 * registered later would miss it entirely. That failure is invisible in development,
 * because a backgrounded app has its listener attached already — and cold is the COMMON
 * case for the pushes that matter most (a draft starting).
 *
 * Verified in the plugin source rather than assumed: both platforms emit this event with
 * `retainUntilConsumed: true` --
 *   ios/Plugin/FirebaseMessagingPlugin.swift:217
 *   android/.../FirebaseMessagingPlugin.java:349
 * -- so Capacitor RETAINS the event and replays it to the first listener that attaches.
 * Registering at module init is therefore sufficient for cold start, and no
 * getDeliveredNotifications() sweep is needed.
 *
 * If that ever changes upstream, cold taps go silently dead. `handleNotificationTap` is
 * exported and tested directly so the routing half keeps its own proof regardless.
 *
 * ── ACTIVE LEAGUE MUST BE SET BEFORE NAVIGATING ─────────────────────────────
 *
 * The NFL shell has no league id in its path; it renders whichever league is active
 * (sf_active_nfl_league_id, SAB-577). Navigating first and setting the league second means
 * the shell mounts against the OLD league and then swaps underneath the user — and a push
 * about League B renders League A's roster under League B's headline. So the write happens
 * first, unconditionally, and the navigation second.
 */
import { persistNflActiveLeagueId } from '@/lib/nflActiveLeague';
import {
  routeForNotification,
  leagueIdForNotification,
  type NotificationData,
} from '@/lib/nflNotificationRoutes';

export interface TapHandlerDeps {
  /** Persist the active NFL league. Injected so tests can observe ORDER, not just calls. */
  setActiveLeague: (leagueId: string) => void;
  /** Perform the navigation. */
  navigate: (route: string) => void;
}

export interface TapResult {
  routed: boolean;
  route: string | null;
  leagueId: string | null;
}

/**
 * Decide and act on a tapped notification.
 *
 * Returns what it did so callers (and tests) can assert on the outcome rather than on
 * console output. `routed: false` means the landing chain should decide, which is the
 * pre-existing behaviour.
 */
export function handleNotificationTap(
  data: NotificationData | null | undefined,
  deps: TapHandlerDeps,
): TapResult {
  const route = routeForNotification(data);
  const leagueId = leagueIdForNotification(data);

  if (!route) {
    // Unknown, deliberately-unrouted, or league-less. Fall through to the landing chain.
    //
    // The active league is NOT set here. A notification we cannot route is not evidence
    // the user wants to switch leagues, and switching silently would change what their
    // next screen shows for a tap that appeared to do nothing.
    return { routed: false, route: null, leagueId };
  }

  // ORDER IS LOAD-BEARING. See the module header: the shell reads the active league at
  // mount, so this must land before the navigation.
  if (leagueId) deps.setActiveLeague(leagueId);
  deps.navigate(route);

  return { routed: true, route, leagueId };
}

/** Default dependency wiring: persist via SAB-577's helper, navigate by assigning location. */
export function defaultTapDeps(): TapHandlerDeps {
  return {
    setActiveLeague: (leagueId: string) => persistNflActiveLeagueId(leagueId),
    // A full assignment rather than a router push: this runs outside React (the plugin
    // listener is registered at module init, before any Router exists on a cold start),
    // so there is no history object to push onto.
    navigate: (route: string) => { globalThis.location.assign(route); },
  };
}
